import { useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { makeApi } from '../../lib/api';
import { DatasourceRefPicker, isDatasourceRef, type DatasourceRef } from './DatasourceRefPicker';
import { useAuthStore } from '../auth/authStore';

/**
 * Phase 3 — structured block builder.
 *
 * Renders the parsed Story content as an editable form:
 *  - Top-level scalars become text/number/boolean inputs.
 *  - Top-level arrays-of-objects (e.g. `content.body[]`) become a
 *    reorderable block list with insert/delete and per-block field
 *    forms.
 *  - Nested objects render as grouped sub-sections.
 *
 * **No drag-and-drop yet** — the list uses ↑/↓ buttons because
 * Plate.js + dnd-kit is its own engineering effort. The reorder
 * affordance already covers the workflow ("move this Hero above
 * the FAQ"), and dnd is purely a polish layer on top.
 *
 * The block-type picker reads from
 * `GET /v1/spaces/{slug}/component-blueprints` — the same set
 * `BlueprintLibrary::seed()` installs on every Space.
 *
 * Schema is enforced **server-side** on save (via
 * ComponentSchemaValidator). This UI is deliberately permissive: an
 * editor can produce shapes the server will reject, and that
 * rejection surfaces as the save-mutation's error. Trying to mirror
 * the full JSON Schema rules client-side would double the source of
 * truth — instead, the JSON view stays available as the escape
 * hatch for anything this UI can't express.
 */
export interface BlockListEditorProps {
  /** The parsed content object. Mutations bubble up via onChange. */
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

interface BlueprintEntry {
  name: string;
  label: string;
  group: string | null;
}

export function BlockListEditor({ value, onChange }: BlockListEditorProps): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);

  // Fetch the blueprint catalog so the "Add block" picker has a
  // current list. Cached 5min — blueprints don't change per click.
  const blueprintsQuery = useQuery({
    enabled: token !== null && spaceSlug !== null,
    queryKey: ['blueprints', spaceSlug],
    queryFn: async (): Promise<BlueprintEntry[]> => {
      if (token === null || spaceSlug === null) throw new Error('no auth');
      const api = makeApi(token);
      const result = await api.listBlueprints(spaceSlug);
      if (result.status !== 'ok') throw new Error(`listBlueprints: ${result.status}`);
      return result.data.data.map((b) => ({
        name: b.name,
        label: b.label,
        group: b.group ?? null,
      }));
    },
    staleTime: 5 * 60_000,
  });

  return (
    <ObjectEditor
      value={value}
      blueprints={blueprintsQuery.data ?? []}
      onChange={onChange}
      depth={0}
    />
  );
}

interface ObjectEditorProps {
  value: Record<string, unknown>;
  blueprints: BlueprintEntry[];
  onChange: (next: Record<string, unknown>) => void;
  depth: number;
}

function ObjectEditor({ value, blueprints, onChange, depth }: ObjectEditorProps): JSX.Element {
  // Stable key order: `_component` first, then other keys alphabetical.
  // Editors expect "what block is this?" up top.
  const keys = useMemo(() => {
    const all = Object.keys(value);
    const component = all.includes('_component') ? ['_component'] : [];
    const rest = all.filter((k) => k !== '_component').sort();
    return [...component, ...rest];
  }, [value]);

  return (
    <div className={depth === 0 ? 'flex flex-col gap-3' : 'flex flex-col gap-2 rounded border border-zinc-200 bg-zinc-50 p-2'}>
      {keys.map((key) => (
        <FieldEditor
          key={key}
          fieldName={key}
          fieldValue={value[key]}
          blueprints={blueprints}
          onChange={(nextField) => onChange({ ...value, [key]: nextField })}
          depth={depth}
        />
      ))}
    </div>
  );
}

interface FieldEditorProps {
  fieldName: string;
  fieldValue: unknown;
  blueprints: BlueprintEntry[];
  onChange: (next: unknown) => void;
  depth: number;
}

function FieldEditor({ fieldName, fieldValue, blueprints, onChange, depth }: FieldEditorProps): JSX.Element {
  // `_component` is the immutable type discriminator — show as a
  // read-only chip, not an editable input.
  if (fieldName === '_component') {
    return (
      <p className="text-xs">
        <span className="inline-block rounded bg-zinc-900 px-2 py-0.5 font-mono text-white">
          {String(fieldValue)}
        </span>
      </p>
    );
  }

  // Array of objects → block list with reorder/delete/add.
  if (Array.isArray(fieldValue) && fieldValue.every((v) => isPlainObject(v))) {
    return (
      <BlockArray
        fieldName={fieldName}
        items={fieldValue as Record<string, unknown>[]}
        blueprints={blueprints}
        onChange={(next) => onChange(next)}
        depth={depth}
      />
    );
  }

  // Array of scalars → comma-separated text input. Editors typing
  // commas in values is a known limitation; the JSON view is the
  // escape hatch.
  if (Array.isArray(fieldValue)) {
    return (
      <Labelled name={fieldName}>
        <input
          type="text"
          value={fieldValue.join(', ')}
          onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()))}
          className="w-full rounded border border-zinc-300 px-2 py-1 text-xs"
        />
      </Labelled>
    );
  }

  // Phase 11.5 — `$ds` datasource-reference picker. Recognises the
  // value shape { $ds: "...", id: "..." } and replaces the generic
  // ObjectEditor with a two-stage dropdown (datasource → entry).
  // Editors who want to add a new ref to a Story just type
  // `{"$ds":"employees","id":""}` in the JSON tab once; from then
  // on the structured editor's nicer UI takes over.
  if (isDatasourceRef(fieldValue)) {
    return (
      <Labelled name={fieldName}>
        <DatasourceRefPicker
          value={fieldValue}
          onChange={(next: DatasourceRef) => onChange(next)}
        />
      </Labelled>
    );
  }

  if (isPlainObject(fieldValue)) {
    return (
      <Labelled name={fieldName}>
        <ObjectEditor
          value={fieldValue}
          blueprints={blueprints}
          onChange={onChange}
          depth={depth + 1}
        />
      </Labelled>
    );
  }

  if (typeof fieldValue === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={fieldValue}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="font-mono uppercase tracking-wider text-zinc-500">{fieldName}</span>
      </label>
    );
  }

  if (typeof fieldValue === 'number') {
    return (
      <Labelled name={fieldName}>
        <input
          type="number"
          value={fieldValue}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full rounded border border-zinc-300 px-2 py-1 text-xs"
        />
      </Labelled>
    );
  }

  // Default: string / null / undefined. Long strings get a textarea,
  // short ones get a single-line input. Heuristic: any current value
  // with a newline or > 80 chars opens as textarea.
  const stringValue = fieldValue === null || fieldValue === undefined ? '' : String(fieldValue);
  const isMultiline = stringValue.includes('\n') || stringValue.length > 80;

  return (
    <Labelled name={fieldName}>
      {isMultiline ? (
        <textarea
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full rounded border border-zinc-300 px-2 py-1 text-xs leading-relaxed"
        />
      ) : (
        <input
          type="text"
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border border-zinc-300 px-2 py-1 text-xs"
        />
      )}
    </Labelled>
  );
}

function Labelled({ name, children }: { name: string; children: JSX.Element }): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
        {name}
      </span>
      {children}
    </label>
  );
}

interface BlockArrayProps {
  fieldName: string;
  items: Record<string, unknown>[];
  blueprints: BlueprintEntry[];
  onChange: (next: Record<string, unknown>[]) => void;
  depth: number;
}

function BlockArray({ fieldName, items, blueprints, onChange, depth }: BlockArrayProps): JSX.Element {
  const [showPicker, setShowPicker] = useState(false);

  // dnd-kit sensors: pointer for mouse/touch + keyboard for a11y.
  // The keyboard sensor uses arrow keys with Space/Enter to pick up
  // and drop; equivalent UX to the ↑/↓ buttons we keep as a visible
  // fallback.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 5px of motion before drag starts so click events
      // (open picker, click input) still fire on the cards.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Stable ids during a single drag: each block's index serialised to a
  // string. The items array doesn't change while a drag is in flight
  // (no other state updates can mutate it mid-gesture), so the ids
  // map 1:1 to positions for the drag's lifetime. After
  // `onDragEnd` re-orders the array, the next render generates new
  // ids matching the new positions — exactly what dnd-kit expects.
  const ids = useMemo(() => items.map((_, i) => `pos-${i}`), [items]);

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (over === null || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  }

  function move(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    onChange(arrayMove(items, index, target));
  }

  function remove(index: number): void {
    onChange(items.filter((_, i) => i !== index));
  }

  function add(blueprintName: string): void {
    const blueprint = blueprints.find((b) => b.name === blueprintName);
    if (blueprint === undefined) return;
    onChange([...items, scaffoldBlock(blueprint)]);
    setShowPicker(false);
  }

  function updateAt(index: number, next: Record<string, unknown>): void {
    const copy = [...items];
    copy[index] = next;
    onChange(copy);
  }

  // Group blueprints by .group for the picker layout
  const grouped = useMemo(() => {
    const out = new Map<string, BlueprintEntry[]>();
    for (const b of blueprints) {
      const g = b.group ?? 'Other';
      const list = out.get(g) ?? [];
      list.push(b);
      out.set(g, list);
    }
    return [...out.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [blueprints]);

  return (
    <div className="flex flex-col gap-2">
      <header className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          {fieldName} ({items.length})
        </span>
        <button
          type="button"
          onClick={() => setShowPicker((s) => !s)}
          className="rounded border border-zinc-300 bg-white px-2 py-0.5 text-xs font-medium hover:bg-zinc-50"
          data-testid={`add-${fieldName}-block`}
        >
          + Block hinzufügen
        </button>
      </header>

      {showPicker ? (
        <div className="rounded border border-zinc-200 bg-white p-2 shadow-sm">
          {grouped.length === 0 ? (
            <p className="text-xs text-zinc-500">Keine Blueprints in diesem Space.</p>
          ) : (
            grouped.map(([group, blueprintsInGroup]) => (
              <section key={group} className="mb-2 last:mb-0">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                  {group}
                </p>
                <div className="flex flex-wrap gap-1">
                  {blueprintsInGroup.map((b) => (
                    <button
                      key={b.name}
                      type="button"
                      onClick={() => add(b.name)}
                      className="rounded bg-zinc-100 px-2 py-0.5 text-xs text-zinc-800 hover:bg-zinc-200"
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      ) : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((block, index) => (
            <SortableBlockCard
              key={ids[index]}
              id={ids[index]!}
              block={block}
              index={index}
              total={items.length}
              blueprints={blueprints}
              depth={depth}
              onMoveUp={() => move(index, -1)}
              onMoveDown={() => move(index, 1)}
              onRemove={() => remove(index)}
              onChange={(next) => updateAt(index, next)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

/**
 * A single block card hooked up to dnd-kit's `useSortable`. The drag
 * handle (☰) on the left is the only element that listens for drag
 * gestures — making the rest of the card non-draggable means clicks
 * on inputs, the picker, and ↑/↓/✕ buttons still work normally.
 * Keyboard users can focus the handle and press Space to pick up,
 * arrow keys to move, Space again to drop.
 */
function SortableBlockCard(props: {
  id: string;
  block: Record<string, unknown>;
  index: number;
  total: number;
  blueprints: BlueprintEntry[];
  depth: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onChange: (next: Record<string, unknown>) => void;
}): JSX.Element {
  const { id, block, index, total, blueprints, depth, onMoveUp, onMoveDown, onRemove, onChange } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      className="rounded-md border border-zinc-200 bg-white p-2"
      data-testid={`block-${index}`}
    >
      <header className="mb-2 flex items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Block verschieben"
          className="cursor-grab rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs text-zinc-500 hover:bg-zinc-100 active:cursor-grabbing"
          // The drag handle should never submit a parent form even if
          // for some reason we get nested in one — dnd-kit's listeners
          // don't suppress the button's default behaviour.
          onClick={(e) => e.preventDefault()}
        >
          ☰
        </button>
        <span className="rounded bg-zinc-100 px-2 py-0.5 font-mono text-[10px] uppercase text-zinc-700">
          {typeof block['_component'] === 'string' ? block['_component'] : 'unknown'}
        </span>
        <span className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            aria-label="Block nach oben"
            className="rounded border border-zinc-300 bg-white px-2 py-0.5 text-xs hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label="Block nach unten"
            className="rounded border border-zinc-300 bg-white px-2 py-0.5 text-xs hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Block entfernen"
            className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-xs text-red-900 hover:bg-red-100"
          >
            ✕
          </button>
        </span>
      </header>
      <ObjectEditor
        value={block}
        blueprints={blueprints}
        onChange={onChange}
        depth={depth + 1}
      />
    </article>
  );
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Build a minimal new block instance for `blueprintName`. We don't
 * inspect the blueprint schema here — server-side validation will
 * tell the editor what required fields are missing. The starter
 * payload is `{ _component: <name> }` so the editor can see + edit
 * the block immediately.
 */
function scaffoldBlock(blueprint: BlueprintEntry): Record<string, unknown> {
  return { _component: blueprint.name };
}
